/**
 * Onboarding Modal Component
 * Non-blocking guided tour with action triggers
 */

import { useNavigate } from 'react-router-dom';
import { useOnboarding, ONBOARDING_STEPS } from '@/context/OnboardingContext';
import {
  Sparkles,
  Calendar,
  Building,
  Users,
  Compass,
  X,
  ChevronRight,
  Check,
} from 'lucide-react';

const STEP_INFO = [
  { id: 'welcome', icon: Sparkles, title: 'Welcome to Luma', description: 'Your complete tour management solution. Let\'s get you started!' },
  { id: 'create-event', icon: Calendar, title: 'Create Your First Event', description: 'Click the + button above to add your first gig, rehearsal, or tour.' },
  { id: 'add-venue', icon: Building, title: 'Build Your Venue Database', description: 'Add venues where you perform. Click the button below to get started.' },
  { id: 'invite-team', icon: Users, title: 'Invite Your Team', description: 'Add collaborators to your organization. Click below to invite.' },
  { id: 'explore-features', icon: Compass, title: 'Explore More Features', description: 'Discover songs, setlists, equipment tracking, and analytics.' },
];

export function OnboardingModal() {
  const navigate = useNavigate();
  const { isActive, currentStep, completeStep, skip } = useOnboarding();

  if (!isActive || currentStep >= STEP_INFO.length) {
    return null;
  }

  const step = STEP_INFO[currentStep];
  const IconComponent = step.icon;
  const isLastStep = currentStep === STEP_INFO.length - 1;

  const handleNext = () => {
    // Navigate to the next step's route BEFORE completing
    if (!isLastStep) {
      const nextRoute = ONBOARDING_STEPS[currentStep + 1]?.route;
      if (nextRoute) {
        navigate(nextRoute);
      }
    }
    // Then advance the step
    completeStep();
  };

  const handleSkip = () => {
    skip();
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      maxWidth: '320px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
      padding: '20px',
      border: '1px solid #e5e7eb'
    }}>
      <button
        onClick={handleSkip}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '50%'
        }}
      >
        <X size={16} color="#9ca3af" />
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{ 
          background: '#eff6ff', 
          padding: '10px', 
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <IconComponent size={24} color="#2563eb" />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: '#111827' }}>
            {step.title}
          </h3>
          <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
            {STEP_INFO.map((_, i) => (
              <div
                key={i}
                style={{
                  height: '4px',
                  flex: i === currentStep ? 2 : 1,
                  borderRadius: '2px',
                  backgroundColor: i <= currentStep ? '#2563eb' : '#d1d5db',
                  transition: 'all 0.2s'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <p style={{ fontSize: '14px', color: '#4b5563', margin: '0 0 16px 0', lineHeight: '1.5' }}>
        {step.description}
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
          {currentStep + 1}/{STEP_INFO.length}
        </span>
        <button
          onClick={handleNext}
          style={{
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {isLastStep ? (
            <>
              <Check size={14} />
              Finish
            </>
          ) : (
            <>
              {currentStep === 0 ? 'Let\'s Go' : 'Next'}
              <ChevronRight size={14} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}